import { useRef, useState } from 'react';
import generatePDF, { Margin, Resolution } from 'react-to-pdf';
import { useReactToPrint } from 'react-to-print';

const useExportDocuments = () => {
  const componentRef = useRef<any>(undefined);
  const [inProgress, setInProgress] = useState(false);

  // Convert SVG to canvas for PDF generation
  const convertSvgToCanvas = (svg: SVGElement): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      try {
        const rect = svg.getBoundingClientRect();
        const width =
          rect.width || parseInt(svg.getAttribute('width') || '0', 10) || 200;
        const height =
          rect.height || parseInt(svg.getAttribute('height') || '0', 10) || 200;

        if (width === 0 || height === 0) {
          reject(new Error('SVG has zero dimensions'));
          return;
        }

        // Clone the SVG to avoid modifying the original
        const clonedSvg = svg.cloneNode(true) as SVGElement;

        // Ensure SVG has explicit dimensions and viewBox
        clonedSvg.setAttribute('width', width.toString());
        clonedSvg.setAttribute('height', height.toString());
        if (!clonedSvg.getAttribute('viewBox')) {
          clonedSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }

        // Get all computed styles and apply them inline to preserve appearance
        const computedStyle = window.getComputedStyle(svg);
        const styleAttributes: string[] = [];

        // Apply important style properties
        if (computedStyle.fill && computedStyle.fill !== 'none') {
          styleAttributes.push(`fill: ${computedStyle.fill}`);
        }
        if (computedStyle.stroke && computedStyle.stroke !== 'none') {
          styleAttributes.push(`stroke: ${computedStyle.stroke}`);
        }
        if (computedStyle.strokeWidth) {
          styleAttributes.push(`stroke-width: ${computedStyle.strokeWidth}`);
        }

        // Apply styles to the SVG element if needed
        if (styleAttributes.length > 0) {
          const existingStyle = clonedSvg.getAttribute('style') || '';
          clonedSvg.setAttribute(
            'style',
            `${existingStyle}; ${styleAttributes.join('; ')}`,
          );
        }

        // Serialize SVG to string with XML declaration
        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(clonedSvg);

        // Add XML declaration and ensure proper encoding
        if (!svgString.includes('<?xml')) {
          svgString = '<?xml version="1.0" encoding="UTF-8"?>' + svgString;
        }

        // Create data URL with proper encoding
        const svgBlob = new Blob([svgString], {
          type: 'image/svg+xml;charset=utf-8',
        });
        const url = URL.createObjectURL(svgBlob);

        // Create image and draw on canvas
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Set canvas size to match SVG dimensions
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            // Fill white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);

            // Draw the SVG image
            ctx.drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);
            resolve(canvas);
          } else {
            URL.revokeObjectURL(url);
            reject(new Error('Could not get canvas context'));
          }
        };

        img.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG image: ' + error));
        };

        img.src = url;
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleDownloadPdf = async (name: string) => {
    setInProgress(true);
    const invoiceName = `${name + '-' + Date.now()}.pdf`;

    // Store SVG replacements for restoration
    const svgReplacements: Array<{
      original: SVGElement;
      canvas: HTMLCanvasElement;
      parent: Node;
      nextSibling: Node | null;
    }> = [];

    // Apply print-friendly styles before PDF generation
    const originalStyles: { [key: string]: string } = {};
    if (componentRef.current) {
      const element = componentRef.current as HTMLElement;

      // Store original styles
      originalStyles.color = element.style.color || '';
      originalStyles.backgroundColor = element.style.backgroundColor || '';

      // Apply print-friendly styles
      element.style.setProperty('color', '#000000', 'important');
      element.style.setProperty('background-color', '#ffffff', 'important');

      // Apply styles to all child elements
      const allElements = element.querySelectorAll('*');
      allElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.setProperty('color', '#000000', 'important');
        htmlEl.style.setProperty('background-color', '#ffffff', 'important');
        htmlEl.style.setProperty('background', '#ffffff', 'important');
        htmlEl.style.setProperty('border-color', 'lightgrey', 'important');
      });

      // Convert all SVGs to canvas elements
      const svgElements = element.querySelectorAll('svg');
      const conversionPromises = Array.from(svgElements).map(async (svg) => {
        try {
          const canvas = await convertSvgToCanvas(svg);

          // Copy styles from SVG to canvas to maintain layout
          const computedStyle = window.getComputedStyle(svg);
          const rect = svg.getBoundingClientRect();

          canvas.style.width = `${rect.width}px`;
          canvas.style.height = `${rect.height}px`;
          canvas.style.display = computedStyle.display;
          canvas.style.margin = computedStyle.margin;
          canvas.style.padding = computedStyle.padding;
          canvas.style.verticalAlign = computedStyle.verticalAlign;
          canvas.style.position = computedStyle.position;
          canvas.style.left = computedStyle.left;
          canvas.style.top = computedStyle.top;

          // Store replacement info
          svgReplacements.push({
            original: svg,
            canvas,
            parent: svg.parentNode!,
            nextSibling: svg.nextSibling,
          });

          // Replace SVG with canvas
          svg.parentNode?.replaceChild(canvas, svg);
        } catch (error) {
          console.warn('Failed to convert SVG to canvas:', error);
        }
      });

      // Wait for all SVG conversions to complete
      await Promise.all(conversionPromises);

      // Small delay to ensure canvas is rendered
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    try {
      await generatePDF(componentRef, {
        filename: invoiceName,
        method: 'save',
        resolution: Resolution.HIGH,
        page: { format: 'dl', margin: Margin.MEDIUM },
        canvas: {
          mimeType: 'image/png',
          qualityRatio: 1,
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      // Restore original SVGs
      svgReplacements.forEach(({ original, canvas, parent, nextSibling }) => {
        try {
          if (canvas.parentNode === parent) {
            parent.replaceChild(original, canvas);
          } else if (nextSibling && nextSibling.parentNode) {
            nextSibling.parentNode.insertBefore(original, nextSibling);
          } else {
            parent.appendChild(original);
          }
        } catch (error) {
          console.warn('Failed to restore SVG:', error);
        }
      });

      // Restore original styles after PDF generation
      if (componentRef.current) {
        const element = componentRef.current as HTMLElement;
        element.style.color = originalStyles.color;
        element.style.backgroundColor = originalStyles.backgroundColor;

        // Remove forced styles from child elements
        const allElements = element.querySelectorAll('*');
        allElements.forEach((el) => {
          const htmlEl = el as HTMLElement;
          htmlEl.style.removeProperty('color');
          htmlEl.style.removeProperty('background-color');
          htmlEl.style.removeProperty('background');
          htmlEl.style.removeProperty('border-color');
        });
      }
      setInProgress(false);
    }
  };

  const handlePrint = useReactToPrint({
    pageStyle: `
      @page {
        size: portrait;
      }
      * {
        color: #000000 !important;
        background-color: #ffffff !important;
        background: #ffffff !important;
        border-color: lightgrey !important;
      }
    `,
  });

  return { componentRef, handleDownloadPdf, handlePrint, inProgress };
};
export default useExportDocuments;
